import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CardProps } from '../../types';
import { Card as LegacyCard } from './Legacy/Card';

const styles = cssMap({
	card: {
		position: 'relative',
		height: '100%',
		minWidth: '220px',
		maxWidth: '320px',
		borderRadius: token('radius.xlarge'),
		transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	frame: {
		position: 'relative',
		height: '100%',
	},
	cutout: {
		// Remove the 38x6 interior of the 40x8 slot, keeping its 1px rim.
		maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='38' height='6'%3E%3Crect width='38' height='6' rx='3'/%3E%3C/svg%3E"), linear-gradient(black, black)`,
		maskSize: '38px 6px, 100% 100%',
		maskPosition: `center ${token('space.100')}, center`,
		maskRepeat: 'no-repeat, repeat',
		maskComposite: 'exclude',
		// Preserve focus outlines outside the masked card.
		maskClip: 'no-clip',
	},
	cutoutShadow: {
		position: 'absolute',
		insetBlockStart: token('space.100'),
		insetInlineStart: '50%',
		transform: 'translateX(-50%)',
		width: '38px',
		height: '6px',
		borderRadius: token('radius.full'),
		// @ts-expect-error -- The cutout needs an inset shadow, which elevation tokens do not provide.
		boxShadow: `inset 0 1px 2px ${token('color.border')}`,
		pointerEvents: 'none',
	},
	interactive: {
		'&:hover': {
			boxShadow: token('elevation.shadow.overlay'),
			transform: 'translateY(-1px)',
		},
		'@media (prefers-reduced-motion: reduce)': {
			'&:hover': { transform: 'none' },
		},
	},
	pressable: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.0'),
		insetInlineStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		zIndex: 100,
		borderRadius: token('radius.xlarge'),
		backgroundColor: 'transparent',
	},
	surface: {
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.raised'),
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) auto',
		gridTemplateRows: 'auto 1fr auto',
		gridTemplateAreas: '"cover cover" "content content" "footer metadata"',
		rowGap: token('space.150'),
		alignItems: 'end',
		height: '100%',
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
});

export const Card = ({
	xcss,
	hasCutout = false,
	...props
}: CardProps & { hasCutout?: boolean }): React.JSX.Element => {
	if (!fg('rovo_agents_studio_browse_kit_uplift')) {
		return <LegacyCard {...props} xcss={xcss} />;
	}

	const { children, ariaLabel, onClick, isDisabled = false, testId, ...boxProps } = props;

	return (
		<Box
			{...boxProps}
			xcss={cx(styles.card, Boolean(onClick) && !isDisabled && styles.interactive, xcss)}
			testId={testId}
		>
			<Box xcss={cx(styles.frame, hasCutout && styles.cutout)}>
				{onClick ? (
					<Pressable
						xcss={styles.pressable}
						onClick={() => onClick()}
						aria-label={ariaLabel}
						isDisabled={isDisabled}
						data-card-pressable
					/>
				) : null}
				<Box xcss={styles.surface} data-card-surface>
					{children}
				</Box>
			</Box>
			{hasCutout ? <Box xcss={styles.cutoutShadow} aria-hidden="true" /> : null}
		</Box>
	);
};
